import {useCallback, useMemo} from 'react'
import {useController, useFormContext} from 'react-hook-form'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {InputSelectPathology} from '@/features/projects/components/input-select-pathology/InputSelectPathology'
import {OtherPathologiesFormSchema} from '@/features/projects/formSchemas'
import {Diagnosis} from '@/features/projects/types'
import {DEFAULT_OTHER_PATHOLOGY_VALUES} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'

interface ProjectOtherPathologiesFieldsProps {
    index: number
    disabledPathologies: string[]
    showTrashButton: boolean
    remove: (index: number) => void
}

export const ProjectOtherPathologiesFields = ({
    index,
    disabledPathologies,
    showTrashButton,
    remove
}: ProjectOtherPathologiesFieldsProps) => {
    const {t} = useTranslation()
    const {control} = useFormContext<OtherPathologiesFormSchema>()
    const {field} = useController({control, name: `otherPathologies.${index}`})

    const selectedPathology = useMemo(() => {
        return field.value
            ? {
                  id: field.value.diagnosisId,
                  description: field.value.description,
                  code: field.value.code
              }
            : null
    }, [field.value])

    const handleSelectPathology = useCallback(
        (value: Diagnosis | null) => {
            if (!value) {
                field.onChange(DEFAULT_OTHER_PATHOLOGY_VALUES)
                return
            }

            field.onChange({
                diagnosisId: value.id,
                description: value.description,
                code: value.code
            })
        },
        [field.onChange, field.value]
    )

    return (
        <Flexbox fullWidth justify="space-between" gap={1} align="flex-end">
            <InputSelectPathology
                label={t('projects:pathology_number', {number: index + 1})}
                placeholder={t('projects:pathology_number_placeholder')}
                value={selectedPathology}
                onChange={handleSelectPathology}
                disabledOptions={disabledPathologies}
            />
            {showTrashButton && (
                <Button variant="ghost" shape="square" onClick={() => remove(index)}>
                    <Trash01Icon />
                </Button>
            )}
        </Flexbox>
    )
}
